import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { getAllWarehouses, getAssignedWarehouses, getWarehousesForUser, type WarehouseResponse } from '@/api/warehouse.api';
import type { WarehouseType } from '@/types/shared';
import SignOutConfirmModal from './SignOutCOnfirmModal';
import { useWarehouseStore } from '@/stores/warehouseStore';

const breadcrumbMap: Record<string, string[]> = {
  '/': ['Dashboard'],
  '/inventory': ['Inventory', 'All Items'],
  '/inventory/new': ['Inventory', 'Add Item'],
  '/inventory/receiving': ['Inventory', 'Stock Receiving'],
  '/inventory/stock': ['Inventory', 'Stock Summary'],
  '/inventory/transfer': ['Inventory', 'Stock Transfer'],
  '/inventory/adjustment': ['Inventory', 'Stock Adjustment'],
  '/inventory/stock-entries': ['Inventory', 'Stock Entries'],
  '/inventory/serials': ['Inventory', 'Serial Numbers'],
  '/inventory/serial-tracking': ['Inventory', 'Serial Traceability'],
  '/billing': ['Billing', 'All Bills'],
  '/billing/new': ['Billing', 'New Bill'],
  '/sales/invoices': ['Sales', 'Invoices'],
  '/sales/invoices/new': ['Sales', 'New Invoice'],
  '/sales/challans': ['Sales', 'Challans'],
  '/sales/returns': ['Sales', 'Returns'],
  '/sales/orders': ['Sales', 'Sales Orders'],
  '/sales/quotations': ['Sales', 'Quotations'],
  '/purchase/orders': ['Purchase', 'Orders'],
  '/purchase/invoices': ['Purchase', 'Invoices'],
  '/purchase/invoices/new': ['Purchase', 'New Invoice'],
  '/purchase/returns': ['Purchase', 'Returns'],
  '/purchase/grn': ['Purchase', 'GRN History'],
  '/purchase/approvals': ['Purchase', 'Approvals'],
  '/reports': ['Reports'],
  // Masters
  '/masters/items': ['Masters', 'Items'],
  '/masters/parties': ['Masters', 'Parties'],
  '/masters/warehouses': ['Masters', 'Warehouses'],
  '/masters/categories': ['Masters', 'Categories & Units'],
  '/masters/company': ['Masters', 'Company'],
  '/masters/work-center': ['Masters', 'Work Centers'],
  '/masters/machines': ['Masters', 'Machines'],
  '/masters/operators': ['Masters', 'Operators'],
  '/masters/shifts': ['Masters', 'Shifts'],
  '/masters/cost-center': ['Masters', 'Cost Centers'],
  '/masters/routing': ['Masters', 'Routing'],
  '/masters/routing/form': ['Masters', 'Routing', 'Form'],
  '/masters/rejection-codes': ['Masters', 'Rejection Codes'],
  '/masters/downtime-codes': ['Masters', 'Downtime Codes'],
  '/masters/quality-parameters': ['Masters', 'Quality Parameters'],
  '/masters/inspection-checklists': ['Masters', 'Inspection Checklists'],
  // Manufacturing
  '/manufacturing/bom': ['Manufacturing', 'Bill of Materials'],
  '/manufacturing/production-orders': ['Manufacturing', 'Production Orders'],
  '/manufacturing/production-orders/new': ['Manufacturing', 'Production Orders', 'New'],
  '/manufacturing/work-orders': ['Manufacturing', 'Work Orders'],
  '/manufacturing/schedule': ['Manufacturing', 'Production Schedule'],
  '/manufacturing/mrp': ['Manufacturing', 'MRP Run'],
  '/manufacturing/mrp/shortage': ['Manufacturing', 'Material Shortage'],
  '/manufacturing/mrp/purchase': ['Manufacturing', 'Purchase Suggestions'],
  '/manufacturing/shop-floor': ['Manufacturing', 'Shop Floor Entry'],
  '/manufacturing/downtime': ['Manufacturing', 'Downtime Entry'],
  '/manufacturing/live': ['Manufacturing', 'Live Dashboard'],
  '/manufacturing/costing': ['Manufacturing', 'Costing Dashboard'],
  '/manufacturing/jobwork': ['Manufacturing', 'Job Work'],
  '/manufacturing/traceability': ['Manufacturing', 'Traceability'],
  '/manufacturing/tools': ['Manufacturing', 'Tool Management'],
  '/manufacturing/maintenance': ['Manufacturing', 'Maintenance'],
  '/manufacturing/qc/incoming': ['Manufacturing', 'QC', 'Incoming QC'],
  '/manufacturing/qc/inprocess': ['Manufacturing', 'QC', 'In-Process QC'],
  '/manufacturing/qc/final': ['Manufacturing', 'QC', 'Final QC'],
  '/manufacturing/qc/ncr': ['Manufacturing', 'QC', 'NCR / CAPA'],
  '/settings': ['Settings'],
  '/users': ['Users'],
};

function resolveBreadcrumbs(pathname: string): string[] {
  if (breadcrumbMap[pathname]) return breadcrumbMap[pathname];
  // Check exact prefix matches
  for (const [key, val] of Object.entries(breadcrumbMap)) {
    if (key !== '/' && pathname.startsWith(key)) {
      return val;
    }
  }
  // Fallback: title-case URL segments
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return ['Dashboard'];
  return parts.map((seg) =>
    seg
      .split('-')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
  );
}

const TYPE_BADGE: Record<WarehouseType, { label: string; cls: string; icon: string }> = {
  OFFICE: { label: 'Office', cls: 'bg-sky-100 text-sky-700', icon: 'ri-building-line' },
  FACTORY: { label: 'Factory', cls: 'bg-amber-100 text-amber-700', icon: 'ri-building-4-line' },
  STORE: { label: 'Store', cls: 'bg-green-100 text-green-700', icon: 'ri-store-2-line' },
  GODOWN: { label: 'Godown', cls: 'bg-violet-100 text-violet-700', icon: 'ri-store-3-line' },
  BRANCH: { label: 'Branch', cls: 'bg-indigo-100 text-indigo-700', icon: 'ri-map-pin-line' },
  TRANSIT: { label: 'Transit', cls: 'bg-slate-100 text-slate-600', icon: 'ri-truck-line' },
};

import { fetchMaintenanceNotifications, type MaintenanceAlertItem } from '@/services/notification.service';
import { getMyNotifications, markNotificationRead, markAllNotificationsRead } from '@/api/notifications.api';

export default function Topbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser, logout } = useAuth();
  const { selectedWarehouseId, selectedWarehouseName, setSelectedWarehouse } = useWarehouseStore();
  const [showWarehouseDropdown, setShowWarehouseDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [warehouses, setWarehouses] = useState<Array<{ id: string; name: string; type: WarehouseType; isPrimary?: boolean; isActive: boolean; gstin: string; state: string; address: string }>>([]);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Notification State: the user's own notifications (e.g. a work order assigned to them)
  const [notifications, setNotifications] = useState<Array<Omit<MaintenanceAlertItem, 'type'> & { type: string }>>([]);
  const [readNotifIds, setReadNotifIds] = useState<Set<string>>(new Set());

  // Loaded on open and refreshed every minute
  useEffect(() => {
    let mounted = true;
    const loadNotifications = () => getMyNotifications()
      .then(({ data }) => {
        if (!mounted) return;
        setNotifications(data.map((n) => ({
          id: n.id,
          type: n.type,
          title: n.title,
          message: n.message || n.title,
          badgeColor: 'blue' as const,
          link: n.link || '/',
          timestamp: new Date(n.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }),
        })));
        setReadNotifIds(new Set(data.filter((n) => n.read_at).map((n) => n.id)));
      })
      .catch(() => { /* bell stays as it is */ });
    loadNotifications();
    const timer = setInterval(loadNotifications, 60000);
    return () => { mounted = false; clearInterval(timer); };
  }, []);

  const whDropdownRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notifDropdownRef = useRef<HTMLDivElement>(null);

  // Maintenance notifications are displayed on the Maintenance module page notification icon
  const unreadAlerts = useMemo(() => {
    return notifications.filter((n) => !readNotifIds.has(n.id));
  }, [notifications, readNotifIds]);

  const { hasControl } = useAuth();
  const canViewAll = hasControl("viewAllWarehouses");

  const isSuperAdmin = authUser?.role === 'SUPER_ADMIN';

  useEffect(() => {
    let mounted = true;
    void (async () => {
      try {
        const res = await getWarehousesForUser(canViewAll);
        if (!mounted) return;
        const rows = (res.data ?? []).map((w: WarehouseResponse) => ({
          id: w.id,
          name: w.name,
          type: w.type,
          isPrimary: (w as any).is_primary ?? false,
          isActive: w.is_active,
          gstin: w.gstin ?? '',
          state: w.state ?? '',
          address: w.address ?? '',
        }));
        setWarehouses(rows);
      } catch {
        if (mounted) setWarehouses([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Determine accessible warehouses
  const accessibleWarehouses = (() => {
    if (isSuperAdmin) return warehouses.filter((w) => w.isActive);
    return warehouses.filter((w) => w.isActive);
  })();

  // Initialize selected warehouse
  useEffect(() => {
    if (accessibleWarehouses.length === 0) return;

    // ✅ Check if current selection is still valid
    const isValid = accessibleWarehouses.some(
      w => w.id === selectedWarehouseId
    );

    if (isValid) return; // keep existing

    let selected;

    // Case 1: only one
    if (accessibleWarehouses.length === 1) {
      selected = accessibleWarehouses[0];
    } else {
      // Case 2: primary
      selected = accessibleWarehouses.find(w => w.isPrimary);

      // Case 3: fallback
      if (!selected) selected = accessibleWarehouses[0];
    }

    if (selected) {
      setSelectedWarehouse(selected.id, selected.name, selected.gstin, selected.state, selected.address);
    }

  }, [accessibleWarehouses, selectedWarehouseId]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!whDropdownRef.current?.contains(e.target as Node)) setShowWarehouseDropdown(false);
      if (!userMenuRef.current?.contains(e.target as Node)) setShowUserMenu(false);
      if (!notifDropdownRef.current?.contains(e.target as Node)) setShowNotifDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedWarehouse = accessibleWarehouses.find((w) => w.id === selectedWarehouseId);
  const selectedType = selectedWarehouse?.type as WarehouseType | undefined;
  const selectedBadge = selectedType ? TYPE_BADGE[selectedType] : null;

  const crumbs = resolveBreadcrumbs(location.pathname);

  // ── Real logout: call context logout then redirect ───────────────────────
  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    await logout();
    navigate('/login');
  };

  const userInitials = authUser?.name
    ? authUser.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()
    : 'U';

  return (
    <>
      <header className="h-[60px] bg-white border-b border-[#e2e8f0] flex items-center px-6 gap-4 shrink-0 relative z-30">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1.5 flex-1">
          {crumbs.map((crumb, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              {idx > 0 && (
                <div className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-arrow-right-s-line text-[#64748b] text-sm" />
                </div>
              )}
              <span
                className={`text-sm font-medium whitespace-nowrap ${idx === crumbs.length - 1 ? 'text-[#1e293b]' : 'text-[#64748b]'
                  }`}
              >
                {crumb}
              </span>
            </div>
          ))}
        </div>

        {/* Warehouse selector */}
        <div className="relative" ref={whDropdownRef}>
          <button
            onClick={() => {
              setShowWarehouseDropdown((p) => !p);
              setShowUserMenu(false);
            }}
            className="flex items-center gap-2 h-9 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] font-medium hover:border-[#4f46e5] hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            {selectedBadge ? (
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${selectedBadge.icon} text-[#4f46e5] text-sm`} />
              </div>
            ) : (
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-store-3-line text-[#4f46e5] text-sm" />
              </div>
            )}
            <span className="max-w-[140px] truncate">{selectedWarehouse?.name ?? 'Select Warehouse'}</span>
            {selectedBadge && (
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${selectedBadge.cls}`}>
                {selectedBadge.label}
              </span>
            )}
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-down-s-line text-[#64748b] text-sm" />
            </div>
          </button>

          {showWarehouseDropdown && (
            <div className="absolute right-0 top-11 w-64 bg-white border border-[#e2e8f0] rounded-xl py-1 z-50">
              <div className="px-3 py-2 border-b border-[#f1f5f9]">
                <p className="text-[10px] font-bold text-[#94a3b8] uppercase tracking-wide">
                  {isSuperAdmin ? 'All Warehouses' : 'Your Assigned Warehouses'}
                </p>
              </div>
              <div className="max-h-64 overflow-y-auto">
                {accessibleWarehouses.map((wh) => {
                  const badge = TYPE_BADGE[wh.type as WarehouseType];
                  const isSelected = wh.id === selectedWarehouseId;
                  return (
                    <button
                      key={wh.id}
                      onClick={() => {
                        setSelectedWarehouse(wh.id, wh.name, wh.gstin, wh.state, wh.address); // ← write to store
                        setShowWarehouseDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 hover:bg-[#f8fafc] transition-colors cursor-pointer ${isSelected ? 'bg-indigo-50' : ''
                        }`}
                    >
                      <div className={`w-7 h-7 flex items-center justify-center rounded-lg ${badge?.cls ?? 'bg-slate-100'}`}>
                        <i className={`${badge?.icon ?? 'ri-store-3-line'} text-sm`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-[#4f46e5]' : 'text-[#1e293b]'}`}>
                          {wh.name}
                        </p>
                        <p className="text-[10px] text-[#94a3b8]">{badge?.label ?? wh.type}</p>
                      </div>
                      {isSelected && (
                        <div className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-[#4f46e5] text-sm" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Notifications & Maintenance Alert Bell */}
        <div className="relative" ref={notifDropdownRef}>
          <button
            onClick={() => {
              setShowNotifDropdown((prev) => !prev);
              setShowWarehouseDropdown(false);
              setShowUserMenu(false);
            }}
            className="relative w-9 h-9 flex items-center justify-center rounded-lg border border-[#e2e8f0] hover:border-[#4f46e5] hover:bg-indigo-50 transition-colors cursor-pointer"
            title="Notifications & System Alerts"
          >
            <i className="ri-notification-3-line text-[#64748b] text-base" />
            {unreadAlerts.length > 0 && (
              <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[10px] font-bold bg-rose-500 text-white rounded-full min-w-[18px] text-center leading-none shadow-sm animate-pulse">
                {unreadAlerts.length}
              </span>
            )}
          </button>

          {showNotifDropdown && (
            <div className="absolute right-0 top-11 w-80 sm:w-96 bg-white border border-slate-200 rounded-xl shadow-xl py-1 z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
                <div className="flex items-center gap-2">
                  <i className="ri-notification-3-fill text-indigo-600 text-base" />
                  <span className="text-sm font-bold text-slate-800">Notifications & Alerts</span>
                  {unreadAlerts.length > 0 && (
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-rose-100 text-rose-700 rounded-full border border-rose-200">
                      {unreadAlerts.length} New
                    </span>
                  )}
                </div>
                {unreadAlerts.length > 0 && (
                  <button
                    onClick={() => {
                      const allIds = new Set(notifications.map((n) => n.id));
                      setReadNotifIds(allIds);
                      void markAllNotificationsRead().catch(() => {});
                    }}
                    className="text-xs text-indigo-600 font-semibold hover:underline cursor-pointer"
                  >
                    Mark all read
                  </button>
                )}
              </div>

              <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                {notifications.length === 0 ? (
                  <div className="p-6 text-center text-slate-400">
                    <i className="ri-checkbox-circle-line text-3xl text-emerald-500 block mb-1" />
                    <p className="text-sm font-medium">All caught up!</p>
                    <p className="text-xs">No pending work order or contract alerts.</p>
                  </div>
                ) : (
                  notifications.map((alert) => {
                    const isUnread = !readNotifIds.has(alert.id);
                    return (
                      <div
                        key={alert.id}
                        onClick={() => {
                          setReadNotifIds((prev) => new Set(prev).add(alert.id));
                          if (isUnread) void markNotificationRead(alert.id).catch(() => {});
                          setShowNotifDropdown(false);
                          navigate(alert.link);
                        }}
                        className={`p-3.5 hover:bg-slate-50 transition-colors cursor-pointer flex items-start gap-3 ${
                          isUnread ? 'bg-indigo-50/20' : 'opacity-80'
                        }`}
                      >
                        <div className="mt-1 shrink-0">
                          {alert.badgeColor === 'red' && (
                            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" />
                          )}
                          {alert.badgeColor === 'yellow' && (
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />
                          )}
                          {alert.badgeColor === 'blue' && (
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-xs leading-relaxed font-semibold ${
                            alert.badgeColor === 'red' ? 'text-slate-900' : 'text-slate-800'
                          }`}>
                            {alert.message}
                          </p>
                          <p className="text-[10px] text-slate-400 mt-1 font-medium flex items-center justify-between">
                            <span>{alert.title}</span>
                            <span>{alert.timestamp}</span>
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* User avatar */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => {
              setShowUserMenu((p) => !p);
              setShowWarehouseDropdown(false);
            }}
            className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity"
          >
            <div className="w-8 h-8 rounded-full bg-[#4f46e5] flex items-center justify-center text-xs font-bold text-white shrink-0">
              {userInitials}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-xs font-semibold text-[#1e293b] leading-tight">{authUser?.name ?? 'User'}</p>
              <p className="text-[10px] text-[#94a3b8] leading-tight">
                {authUser?.role === 'SUPER_ADMIN'
                  ? 'Super Admin'
                  : authUser?.role === 'SUB_ADMIN'
                    ? 'Manager'
                    : 'Staff'}
              </p>
            </div>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-down-s-line text-[#64748b] text-sm" />
            </div>
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-11 w-52 bg-white border border-[#e2e8f0] rounded-xl py-1 z-50">
              <div className="px-4 py-2.5 border-b border-[#e2e8f0]">
                <p className="text-sm font-semibold text-[#1e293b]">{authUser?.name ?? 'User'}</p>
                <p className="text-xs text-[#64748b]">{authUser?.email ?? ''}</p>
              </div>
              {[
                { icon: 'ri-user-3-line', label: 'My Profile', path: '/users' },
                { icon: 'ri-settings-3-line', label: 'Settings', path: '/settings' },
              ].map((item) => (
                <button
                  key={item.label}
                  onClick={() => { navigate(item.path); setShowUserMenu(false); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-[#1e293b] flex items-center gap-2 hover:bg-[#f8fafc] transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${item.icon} text-[#64748b] text-sm`} />
                  </div>
                  {item.label}
                </button>
              ))}
              <div className="border-t border-[#e2e8f0] mt-1">
                <button
                  onClick={() => { setShowUserMenu(false); setShowLogoutConfirm(true); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-500 flex items-center gap-2 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-logout-box-r-line text-sm" />
                  </div>
                  Sign Out
                </button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Sign-out confirmation modal */}
      <SignOutConfirmModal
        open={showLogoutConfirm}
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutConfirm(false)}
      />
    </>
  );
}